import { lineMetricsAtOffset } from "./lineMetrics.js";

function resolveLineHeightPx(cs: CSSStyleDeclaration): number {
  const lh = cs.lineHeight;
  if (lh.endsWith("px")) {
    const n = parseFloat(lh);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const fs = parseFloat(cs.fontSize) || 14;
  return fs * 1.45;
}

/**
 * Rectangular (column) selection overlay for the plain-text textarea.
 * Paints one semi-transparent band per selected row over the editor host.
 * The overlay element is absolutely positioned inside the host with pointer-events: none.
 */
export class RectSelectOverlay {
  private readonly el: HTMLDivElement;
  private bands: HTMLDivElement[] = [];
  /** Cached monospace char width in px (measured once per drag). */
  private charW: number | null = null;
  /** Last paint args, used by {@link repaint}. */
  private lastTa: HTMLTextAreaElement | null = null;
  private lastAnchor = 0;
  private lastFocus = 0;

  constructor(host: HTMLElement) {
    this.el = document.createElement("div");
    this.el.className = "npp-rect-select-overlay";
    this.el.setAttribute("aria-hidden", "true");
    host.appendChild(this.el);
  }

  private measureCharWidth(cs: CSSStyleDeclaration): number {
    if (this.charW !== null) return this.charW;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      this.charW = 8;
      return 8;
    }
    ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    const w = ctx.measureText("0").width;
    this.charW = w > 0 ? w : 8;
    return this.charW;
  }

  paint(ta: HTMLTextAreaElement, anchorOffset: number, focusOffset: number): void {
    this.lastTa = ta;
    this.lastAnchor = anchorOffset;
    this.lastFocus = focusOffset;
    const text = ta.value;
    const a = lineMetricsAtOffset(text, anchorOffset);
    const b = lineMetricsAtOffset(text, focusOffset);
    const l0 = Math.min(a.lineIndex0, b.lineIndex0);
    const l1 = Math.max(a.lineIndex0, b.lineIndex0);
    const c0 = Math.min(a.col1 - 1, b.col1 - 1);
    const c1 = Math.max(a.col1 - 1, b.col1 - 1);
    const lineCount = l1 - l0 + 1;

    const cs = getComputedStyle(ta);
    const padT = parseFloat(cs.paddingTop) || 0;
    const padL = parseFloat(cs.paddingLeft) || 0;
    const rowH = resolveLineHeightPx(cs);
    const charW = this.measureCharWidth(cs);

    const scrollTop = ta.scrollTop;
    const scrollLeft = ta.scrollLeft;

    // Grow band pool as needed (bands are reused across paints).
    while (this.bands.length < lineCount) {
      const band = document.createElement("div");
      band.className = "npp-rect-select-band";
      this.el.appendChild(band);
      this.bands.push(band);
    }

    for (let i = 0; i < this.bands.length; i++) {
      const band = this.bands[i]!;
      if (i < lineCount) {
        const li = l0 + i;
        const top = padT + li * rowH - scrollTop;
        const left = padL + c0 * charW - scrollLeft;
        // Minimum 2 px width so a zero-column cursor is still visible.
        const width = Math.max(2, (c1 - c0 + 1) * charW);
        band.style.cssText = `top:${top}px;left:${left}px;width:${width}px;height:${rowH}px`;
        band.hidden = false;
      } else {
        band.hidden = true;
      }
    }
    this.el.hidden = false;
  }

  /** Re-paint using the last arguments passed to {@link paint} (e.g. after scroll). */
  repaint(): void {
    if (this.lastTa) this.paint(this.lastTa, this.lastAnchor, this.lastFocus);
  }

  hide(): void {
    this.el.hidden = true;
  }

  /** Remove the overlay element from the DOM entirely. */
  remove(): void {
    this.el.remove();
  }
}
